import {
    LiveKitRoom,
    VideoConference,
    GridLayout,
    ParticipantTile,
    useTracks,
    useParticipants,
    useLocalParticipant,
    useConnectionState,
    useDataChannel,
    useRoomContext,
    ControlBar,
    Chat,
    VideoTrack,
    DisconnectButton,
    RoomName
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import http from '../../utils/http';
import { TypedLiveKitRoom, TypedDisconnectButton, TypedVideoTrack } from './utils/livekitTyped';
import { 
  startLiveKitRecording, 
  stopLiveKitRecording, 
  getRecordingStatus 
} from '../../api/livekitRecording';

interface TempleLiveKitMeetingProps {
    appointmentId: string;
    liveKitResponse: any;
    setDidCallEnd: (value: boolean) => void;
    patientName: string;
    setNoteGenerationStatus: (status: 'ai-generation' | 'manual') => void;
    setFileName: (fileName: any) => void;
}

export function TempleLiveKitMeeting({
    appointmentId,
    liveKitResponse,
    setDidCallEnd,
    patientName,
    setNoteGenerationStatus,
    setFileName
}: TempleLiveKitMeetingProps) {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingId, setRecordingId] = useState<string | null>(null);
    const [recordingType, setRecordingType] = useState<'audio' | 'video'>('audio');
    const [callDuration, setCallDuration] = useState<string>('00:00:00');
    const [showInfo, setShowInfo] = useState(false);
    const startTimeRef = useRef<Date | null>(null);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);


    if (!liveKitResponse?.token || !liveKitResponse?.livekitUrl) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <p className="text-red-500">Failed to load meeting. Please try again.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-full w-full bg-gray-50">
            <TypedLiveKitRoom
                token={liveKitResponse.token}
                serverUrl={liveKitResponse.livekitUrl}
                connect={true}
                audio={true}
                video={true}
                onDisconnected={() => setDidCallEnd(true)}
            >
                <MeetingContent
                    appointmentId={appointmentId}
                    patientName={patientName}
                    setFileName={setFileName}
                    setDidCallEnd={setDidCallEnd}
                    setNoteGenerationStatus={setNoteGenerationStatus}
                    isRecording={isRecording}
                    recordingId={recordingId}
                    setIsRecording={setIsRecording}
                    setRecordingId={setRecordingId}
                    recordingType={recordingType}
                    setRecordingType={setRecordingType}
                    callDuration={callDuration}
                    setCallDuration={setCallDuration}
                    liveKitResponse={liveKitResponse}
                />
            </TypedLiveKitRoom>
        </div>
    );
}

function MeetingContent({
    appointmentId,
    patientName,
    setFileName,
    setDidCallEnd,
    setNoteGenerationStatus,
    isRecording,
    recordingId,
    setIsRecording,
    setRecordingId,
    recordingType,
    setRecordingType,
    callDuration,
    setCallDuration,
    liveKitResponse
}: any) {
    const connectionState = useConnectionState();
    const localParticipant = useLocalParticipant();
    const participants = useParticipants();
    const room = useRoomContext();
    const tracks = useTracks();
    const videoTracks = tracks.filter(track => track.publication.kind === Track.Kind.Video);
    const startTimeRef = useRef<Date | null>(null);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [showInfo, setShowInfo] = useState(false);

    // Format call duration
    const formatDuration = (startTime: Date) => {
        const now = new Date();
        const diff = now.getTime() - startTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Start call duration timer
    useEffect(() => {
        if (connectionState === 'connected' && !startTimeRef.current) {
            startTimeRef.current = new Date();
            durationIntervalRef.current = setInterval(() => {
                if (startTimeRef.current) {
                    setCallDuration(formatDuration(startTimeRef.current));
                }
            }, 1000);
        }

        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        };
    }, [connectionState]);

    // Start recording
    const startRecording = async () => {
        try {
            const response = await startLiveKitRecording({
                appointmentId,
                recordingType
            });

            setIsRecording(true);
            setRecordingId(response.recordingId);
            toast.success(`${recordingType.charAt(0).toUpperCase() + recordingType.slice(1)} recording started`);
        } catch (error: any) {
            console.error('Error starting recording:', error);
            toast.error(error.response?.data?.error || 'Failed to start recording');
        }
    };

    // Stop recording
    const stopRecording = async () => {
        try {
            const response = await stopLiveKitRecording({
                appointmentId,
                egressId: recordingId || undefined
            });

            setIsRecording(false);
            setRecordingId(null);

            if (response.fileUrl) {
                setFileName({
                    url: response.fileUrl,
                    name: `${recordingType}-recording-${appointmentId}-${Date.now()}`
                });
            }

            toast.success(`${recordingType.charAt(0).toUpperCase() + recordingType.slice(1)} recording stopped and saved`);
        } catch (error: any) {
            console.error('Error stopping recording:', error);
            toast.error(error.response?.data?.error || 'Failed to stop recording');
        }
    };

    // Toggle recording
    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    // Get room participants
    const getParticipants = async () => {
        try {
            const response = await http.get(`/livekit/${appointmentId}/participants`);
            console.log('Room participants:', response.data);
        } catch (error) {
            console.error('Error getting participants:', error);
        }
    };

    useEffect(() => {
        if (connectionState === 'connected') {
            getParticipants();
        }
    }, [connectionState]);

    return (
        <div className="flex flex-col h-screen">
            {/* Header with call info */}
            <div className=" text-white p-4 flex justify-between items-center bg-gray-100 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                    <h2 className="text-lg font-semibold text-gray-800">Your appointment with {patientName}</h2>
                    <span className="text-sm text-gray-600">{callDuration}</span>
                    {isRecording && (
                        <span className="flex items-center space-x-2 text-red-500">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-sm">Recording</span>
                        </span>
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                        className="relative p-2 text-gray-600 hover:text-gray-800"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {showInfo && (
                            <div className="absolute right-0 top-8 w-48 p-2 bg-white border border-gray-200 text-gray-700 text-xs rounded shadow-lg z-10">
                                <p>Meeting ID: {appointmentId}</p>
                                <p>Participants: {participants.length}</p>
                                <p>Connection: {connectionState}</p>
                            </div>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex-1 bg-gray-200 relative">
                {/* <GridLayout tracks={tracks}>
                  <ParticipantTile />
                </GridLayout> */}
                <VideoConference />
                
                {/* Recording controls */}
                <div className="absolute bottom-4 left-4 flex items-center space-x-2 bg-white rounded-lg shadow-lg p-2">
                    {!isRecording && (
                        <div className="flex items-center space-x-2">
                            <select 
                                value={recordingType} 
                                onChange={(e) => setRecordingType(e.target.value as 'audio' | 'video')}
                                className="px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                                <option value="video">Video</option>
                                <option value="audio">Audio</option>
                            </select>
                            <button
                                onClick={startRecording}
                                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 flex items-center space-x-1"
                            >
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                <span>Start {recordingType}</span>
                            </button>
                        </div>
                    )}
                    
                    {isRecording && (
                        <button
                            onClick={stopRecording}
                            className="px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900 flex items-center space-x-1"
                        >
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span>Stop {recordingType}</span>
                        </button>
                    )}
                </div>
                
                {/* Control bar at bottom */}
                {/* <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                    <ControlBar 
                        saveUserChoices={true} 
                        variation='minimal' 
                        controls={{leave: true, camera: true, microphone: true, screenShare: true}} 
                    />
                </div> */}
            </div>

           
        </div>
    );
}
